/**
 * Control Presupuestario → Creators / Influencers (Finance 07/10/2026): el
 * dashboard de FP&A de spend en creadores e influencers, conectado a BigQuery
 * (`financePnl.getCreators`, cache de 10 min). Dos vistas (Creators /
 * Influencers) con período (año o un mes) y "comparar con" otro período. Los
 * cálculos viven en `shared/finance-creators.ts`; acá solo se dibujan.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Bar, BarChart, CartesianGrid, Cell, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { StatCard } from '@/components/ui/stat-card';
import { WinclapLoader } from '@/components/ui/winclap-loader';
import { InternalTable, type InternalColumn } from '@/components/internal/shared';
import { trpc } from '@/lib/trpc';
import {
  compareRows,
  computeBreakdown,
  computeCreatorsKpis,
  computeMonthly,
  computeOffer,
  deltaPct,
  periodMonths,
  type CreatorsCompareRow,
  type CreatorsPeriod,
  type CreatorsRow,
} from '../../../../../shared/finance-creators';
import { CREATORS_BUCKETS, type CreatorsBucket, type CreatorsDashboardData, type CreatorsKind } from '../../../../../shared/types/finance-creators';
import { C, usePnlFmt, type PnlFmt } from './pnl-format';
import { Seg, SegLabel, SectionHead } from './pnl-ui';

const YEAR = 2026;
const TOP_CLIENTS = 15;
const BUCKET_COLOR: Record<CreatorsBucket, string> = { ADV: '#0FA999', CBA: '#E06240', AMP: '#8A5CD6', UGC: '#1E8FC2', Otros: '#C99A00' };
const INFLUENCERS_COLOR = '#8A5CD6';

type PeriodValue = 'all' | `${number}`;
const toPeriod = (v: PeriodValue): CreatorsPeriod => (v === 'all' ? 'all' : Number(v));

function PeriodSelect({ value, onChange, months, fmt, allowNone, ariaLabel }: {
  value: PeriodValue | 'none';
  onChange: (v: PeriodValue | 'none') => void;
  months: number[];
  fmt: PnlFmt;
  allowNone?: boolean;
  ariaLabel: string;
}) {
  const { t } = useTranslation();
  return (
    <Select value={value} onValueChange={(v) => onChange(v as PeriodValue | 'none')}>
      <SelectTrigger className="h-8 w-[170px] text-xs" aria-label={ariaLabel}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {allowNone && <SelectItem value="none">{t('budget_control.creators.no_compare')}</SelectItem>}
        <SelectItem value="all">{t('budget_control.creators.full_year')}</SelectItem>
        {months.map((m) => (
          <SelectItem key={m} value={String(m)}>
            {fmt.monthLong(m)} {YEAR}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export function CreatorsDashboard() {
  const { t } = useTranslation();
  const fmt = usePnlFmt(YEAR);
  const utils = trpc.useUtils();
  const query = trpc.financePnl.getCreators.useQuery({ year: YEAR }, { staleTime: 5 * 60 * 1000, retry: false });
  const [kind, setKind] = useState<CreatorsKind>('creators');
  const [periodSel, setPeriodSel] = useState<PeriodValue>('all');
  const [compareSel, setCompareSel] = useState<PeriodValue | 'none'>('none');
  const [refreshing, setRefreshing] = useState(false);

  const refresh = async () => {
    setRefreshing(true);
    try {
      const fresh = await utils.financePnl.getCreators.fetch({ year: YEAR, refresh: true });
      utils.financePnl.getCreators.setData({ year: YEAR }, fresh);
      toast.success(t('budget_control.pnl.refreshed'));
    } catch (e) {
      toast.error(t('budget_control.pnl.refresh_failed', { error: e instanceof Error ? e.message : String(e) }));
    } finally {
      setRefreshing(false);
    }
  };

  if (query.isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <WinclapLoader size={40} />
      </div>
    );
  }
  if (query.error || !query.data) {
    return (
      <Alert variant="destructive">
        <AlertTitle>{t('budget_control.creators.load_error_title')}</AlertTitle>
        <AlertDescription>
          {t('budget_control.creators.load_error_desc')} {query.error?.message}
        </AlertDescription>
      </Alert>
    );
  }
  const data = query.data;
  if (data.lastMonth < 1) {
    return (
      <Alert>
        <AlertTitle>{t('budget_control.creators.no_data_title')}</AlertTitle>
        <AlertDescription>{t('budget_control.creators.no_data_desc', { year: data.year })}</AlertDescription>
      </Alert>
    );
  }

  const months = periodMonths('all', data.lastMonth);
  const period = toPeriod(periodSel);
  const compare = compareSel === 'none' ? null : toPeriod(compareSel);
  const label = (p: CreatorsPeriod) => (p === 'all' ? t('budget_control.creators.full_year_short', { last: fmt.monthShort(data.lastMonth) }) : fmt.monthShort(p));

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end gap-4">
        <Seg
          value={kind}
          onChange={setKind}
          ariaLabel={t('budget_control.creators.kind_aria')}
          options={[
            { value: 'creators', label: t('budget_control.creators.kind_creators') },
            { value: 'influencers', label: t('budget_control.creators.kind_influencers') },
          ]}
        />
        <div className="flex flex-col gap-1">
          <SegLabel>{t('budget_control.creators.period')}</SegLabel>
          <PeriodSelect value={periodSel} onChange={(v) => v !== 'none' && setPeriodSel(v)} months={months} fmt={fmt} ariaLabel={t('budget_control.creators.period')} />
        </div>
        <div className="flex flex-col gap-1">
          <SegLabel>{t('budget_control.creators.compare')}</SegLabel>
          <PeriodSelect value={compareSel} onChange={setCompareSel} months={months} fmt={fmt} allowNone ariaLabel={t('budget_control.creators.compare')} />
        </div>
        <div className="ml-auto flex items-center gap-3 text-xs text-muted-foreground">
          <span>{t('budget_control.creators.updated', { date: fmt.dateTime(data.generatedAt) })}</span>
          <Button variant="outline" size="sm" onClick={refresh} disabled={refreshing}>
            <RefreshCw className={refreshing ? 'mr-1.5 h-3.5 w-3.5 animate-spin' : 'mr-1.5 h-3.5 w-3.5'} />
            {t('budget_control.pnl.refresh')}
          </Button>
        </div>
      </div>

      <Kpis data={data} kind={kind} period={period} compare={compare} label={label} fmt={fmt} />
      <Monthly data={data} kind={kind} period={period} compare={compare} fmt={fmt} />
      {kind === 'creators' && <Offer data={data} period={period} compare={compare} label={label} fmt={fmt} />}

      <div className="grid gap-6 lg:grid-cols-2">
        <Breakdown data={data} kind={kind} dim="segment" period={period} compare={compare} label={label} fmt={fmt} />
        <Breakdown data={data} kind={kind} dim="region" period={period} compare={compare} label={label} fmt={fmt} />
      </div>
      <Breakdown data={data} kind={kind} dim="client" period={period} compare={compare} label={label} fmt={fmt} />

      <p className="text-xs text-muted-foreground">{t('budget_control.creators.source_note')}</p>
    </div>
  );
}

interface BlockProps {
  data: CreatorsDashboardData;
  period: CreatorsPeriod;
  compare: CreatorsPeriod | null;
  label: (p: CreatorsPeriod) => string;
  fmt: PnlFmt;
}

const usd = (fmt: PnlFmt, v: number) => `$${fmt.num(v, 0)}`;

function Kpis({ data, kind, period, compare, label, fmt }: BlockProps & { kind: CreatorsKind }) {
  const { t } = useTranslation();
  const a = computeCreatorsKpis(data, kind, period);
  const b = compare === null ? null : computeCreatorsKpis(data, kind, compare);
  const vs = (x: number, y: number, money: boolean) => {
    const d = deltaPct(x, y);
    return `${money ? usd(fmt, x) : fmt.num(x, 0)} → ${money ? usd(fmt, y) : fmt.num(y, 0)} (${fmt.sgnPct(d)})`;
  };
  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
      <StatCard size="sm" label={t('budget_control.creators.kpi_spend')} value={usd(fmt, a.spend)} sub={b ? vs(a.spend, b.spend, true) : label(period)} />
      <StatCard size="sm" label={t('budget_control.creators.kpi_records')} value={fmt.num(a.n, 0)} sub={b ? vs(a.n, b.n, false) : t('budget_control.creators.kpi_records_sub')} />
      <StatCard size="sm" label={t('budget_control.creators.kpi_clients')} value={fmt.num(a.clients, 0)} sub={b ? vs(a.clients, b.clients, false) : t('budget_control.creators.kpi_clients_sub')} />
      {b ? (
        <StatCard size="sm" label={t('budget_control.creators.kpi_vs', { a: label(period), b: label(compare!) })} value={fmt.sgnPct(deltaPct(a.spend, b.spend))} sub={t('budget_control.creators.kpi_vs_sub')} />
      ) : period === 'all' ? (
        <StatCard size="sm" label={t('budget_control.creators.kpi_avg')} value={usd(fmt, a.avg)} sub={t('budget_control.creators.kpi_avg_sub')} />
      ) : (
        <StatCard size="sm" label={t('budget_control.creators.kpi_share')} value={fmt.pct(a.avg)} sub={t('budget_control.creators.kpi_share_sub')} />
      )}
    </div>
  );
}

function Monthly({ data, kind, period, compare, fmt }: Omit<BlockProps, 'label'> & { kind: CreatorsKind }) {
  const { t } = useTranslation();
  const points = computeMonthly(data, kind).map((p) => ({ name: fmt.monthShort(p.month), month: p.month, total: p.total, ...p.buckets }));
  const highlighted = new Set<number>([period, compare].filter((p): p is number => typeof p === 'number'));
  const dim = (m: number) => highlighted.size > 0 && !highlighted.has(m);
  const keys: Array<{ key: string; color: string; name: string }> =
    kind === 'creators' ? CREATORS_BUCKETS.map((b) => ({ key: b, color: BUCKET_COLOR[b], name: b })) : [{ key: 'total', color: INFLUENCERS_COLOR, name: t('budget_control.creators.kpi_spend') }];
  return (
    <section>
      <SectionHead num="01" title={t('budget_control.creators.monthly_title')} sub={t(kind === 'creators' ? 'budget_control.creators.monthly_sub_creators' : 'budget_control.creators.monthly_sub_influencers')} />
      <Card className="p-4 shadow-xs">
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={points} margin={{ left: 8, right: 8 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={C.hair} />
            <XAxis dataKey="name" tick={{ fontSize: 12 }} />
            <YAxis tick={{ fontSize: 11 }} tickFormatter={(v: number) => `$${fmt.num(v / 1000, 0)}k`} width={56} />
            <Tooltip formatter={(v, name) => [usd(fmt, Number(v)), name]} />
            {kind === 'creators' && <Legend wrapperStyle={{ fontSize: 12 }} />}
            {keys.map((k, i) => (
              <Bar key={k.key} dataKey={k.key} name={k.name} stackId="spend" fill={k.color} radius={i === keys.length - 1 ? [3, 3, 0, 0] : undefined}>
                {points.map((p) => (
                  <Cell key={p.month} fillOpacity={dim(p.month) ? 0.3 : 1} />
                ))}
              </Bar>
            ))}
          </BarChart>
        </ResponsiveContainer>
      </Card>
    </section>
  );
}

function CompareTable({ rows, label, a, b, fmt }: { rows: CreatorsCompareRow[]; label: string; a: string; b: string; fmt: PnlFmt }) {
  const { t } = useTranslation();
  const columns: InternalColumn<CreatorsCompareRow>[] = [
    { key: 'name', label, render: (r) => <span className="font-medium">{r.name}</span> },
    { key: 'a', label: a, align: 'right', className: 'tabular-nums', render: (r) => usd(fmt, r.a) },
    { key: 'b', label: b, align: 'right', className: 'tabular-nums', render: (r) => usd(fmt, r.b) },
    {
      key: 'd',
      label: 'Δ%',
      align: 'right',
      className: 'tabular-nums font-semibold',
      render: (r) => (
        <span style={{ color: r.deltaPct === null || Math.abs(r.deltaPct) < 0.5 ? undefined : r.deltaPct > 0 ? 'var(--pnl-unfav)' : 'var(--pnl-fav)' }}>
          {r.deltaPct === null ? t('budget_control.creators.new') : fmt.sgnPct(r.deltaPct)}
        </span>
      ),
    },
  ];
  if (!rows.length) return <p className="py-6 text-center text-sm text-muted-foreground">{t('budget_control.creators.empty')}</p>;
  return <InternalTable columns={columns} rows={rows} rowKey={(r) => r.name} />;
}

function RowsTable({ rows, label, withRevenue, withRecords, fmt, color }: {
  rows: CreatorsRow[];
  label: string;
  withRevenue: boolean;
  withRecords: boolean;
  fmt: PnlFmt;
  color?: (name: string) => string | undefined;
}) {
  const { t } = useTranslation();
  const max = Math.max(1, ...rows.map((r) => r.spend));
  const columns: InternalColumn<CreatorsRow>[] = [
    {
      key: 'name',
      label,
      render: (r) => (
        <span className="flex items-center gap-2 font-medium">
          {color?.(r.name) && <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: color(r.name) }} />}
          {r.name}
        </span>
      ),
    },
    {
      key: 'spend',
      label: t('budget_control.creators.col_spend'),
      align: 'right',
      className: 'tabular-nums',
      render: (r) => (
        <span className="inline-flex items-center justify-end gap-2">
          <span className="h-1.5 w-14 overflow-hidden rounded-full bg-muted">
            <span className="block h-full rounded-full" style={{ width: `${(r.spend / max) * 100}%`, background: '#0FA999' }} />
          </span>
          {usd(fmt, r.spend)}
        </span>
      ),
    },
    { key: 'pct', label: '%', align: 'right', className: 'tabular-nums text-muted-foreground', render: (r) => fmt.pct(r.pct) },
    ...(withRecords
      ? [
          { key: 'n', label: t('budget_control.creators.col_records'), align: 'right' as const, className: 'tabular-nums', render: (r: CreatorsRow) => fmt.num(r.n, 0) },
          { key: 'avg', label: t('budget_control.creators.col_avg'), align: 'right' as const, className: 'tabular-nums text-muted-foreground', render: (r: CreatorsRow) => usd(fmt, r.n ? r.spend / r.n : 0) },
        ]
      : []),
    ...(withRevenue
      ? [
          { key: 'rev', label: t('budget_control.creators.col_revenue'), align: 'right' as const, className: 'tabular-nums', render: (r: CreatorsRow) => (r.revenue === null ? '–' : usd(fmt, r.revenue)) },
          { key: 'ratio', label: t('budget_control.creators.col_ratio'), align: 'right' as const, className: 'tabular-nums', render: (r: CreatorsRow) => fmt.pct(r.ratio) },
        ]
      : []),
  ];
  if (!rows.length) return <p className="py-6 text-center text-sm text-muted-foreground">{t('budget_control.creators.empty')}</p>;
  return <InternalTable columns={columns} rows={rows} rowKey={(r) => r.name} />;
}

function Offer({ data, period, compare, label, fmt }: BlockProps) {
  const { t } = useTranslation();
  const rows = computeOffer(data, period);
  return (
    <section>
      <SectionHead num="02" title={t('budget_control.creators.offer_title')} sub={compare === null ? label(period) : `${label(period)} vs ${label(compare)}`} />
      <Card className="p-4 shadow-xs">
        {compare === null ? (
          <RowsTable rows={rows} label={t('budget_control.creators.col_offer')} withRevenue={false} withRecords fmt={fmt} color={(n) => BUCKET_COLOR[n as CreatorsBucket]} />
        ) : (
          <CompareTable rows={compareRows(rows, computeOffer(data, compare))} label={t('budget_control.creators.col_offer')} a={label(period)} b={label(compare)} fmt={fmt} />
        )}
      </Card>
    </section>
  );
}

const DIM_KEYS = {
  segment: { num: '03', title: 'budget_control.creators.segment_title', col: 'budget_control.creators.col_segment' },
  region: { num: '04', title: 'budget_control.creators.region_title', col: 'budget_control.creators.col_region' },
  client: { num: '05', title: 'budget_control.creators.clients_title', col: 'budget_control.creators.col_client' },
} as const;

function Breakdown({ data, kind, dim, period, compare, label, fmt }: BlockProps & { kind: CreatorsKind; dim: 'segment' | 'region' | 'client' }) {
  const { t } = useTranslation();
  const k = DIM_KEYS[dim];
  const rows = computeBreakdown(data, kind, dim, period);
  const top = dim === 'client' ? rows.slice(0, TOP_CLIENTS) : rows;
  const sub =
    compare !== null
      ? `${label(period)} vs ${label(compare)}`
      : dim === 'client'
        ? t('budget_control.creators.clients_sub', { n: Math.min(rows.length, TOP_CLIENTS), total: rows.length, period: label(period) })
        : t('budget_control.creators.vs_revenue_sub', { period: label(period) });
  return (
    <section>
      <SectionHead num={k.num} title={t(k.title)} sub={sub} />
      <Card className="p-4 shadow-xs">
        {compare === null ? (
          <RowsTable rows={top} label={t(k.col)} withRevenue withRecords={dim === 'client'} fmt={fmt} />
        ) : (
          <CompareTable
            rows={compareRows(rows, computeBreakdown(data, kind, dim, compare)).slice(0, dim === 'client' ? TOP_CLIENTS : undefined)}
            label={t(k.col)}
            a={label(period)}
            b={label(compare)}
            fmt={fmt}
          />
        )}
      </Card>
    </section>
  );
}
